/**
 * FedEx Settings Page
 *
 * Allows users to configure FedEx API credentials, ETD settings,
 * and letterhead/signature images for customs clearance.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  Switch,
  Space,
  message,
  Alert,
  Typography,
  Divider,
  Spin,
  Tag,
  Descriptions,
  Row,
  Col,
  Upload,
  Modal,
  Tabs,
  Select,
  Popconfirm,
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  SendOutlined,
  UploadOutlined,
  FileImageOutlined,
  FileProtectOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { apiService } from '../../../services/apiService';

const { Title, Text, Paragraph, Link } = Typography;
const { TabPane } = Tabs;

interface FedExConfig {
  clientId: string;
  clientSecret?: string;
  accountNumber: string;
  isProduction: boolean;
  isActive: boolean;
  clientSecretSet?: boolean;
  etdEnabled?: boolean;
  letterheadImageIndex?: string;
  signatureImageIndex?: string;
  linkedCustomers?: Array<{ id: string; name: string }>;
}

interface ImageUploadState {
  letterhead: {
    uploading: boolean;
    uploaded: boolean;
    preview: string | null;
  };
  signature: {
    uploading: boolean;
    uploaded: boolean;
    preview: string | null;
  };
}

const FedExSettings: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const configId = searchParams.get('configId') || undefined;
  const isNewMode = searchParams.get('new') === '1';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState<FedExConfig | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [etdEnabled, setEtdEnabled] = useState(true); // ETD enabled by default
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([]);
  const [imageState, setImageState] = useState<ImageUploadState>({
    letterhead: { uploading: false, uploaded: false, preview: null },
    signature: { uploading: false, uploaded: false, preview: null },
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const letterheadInputRef = useRef<HTMLInputElement>(null);
  const signatureInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadCustomers();
    if (configId) {
      loadConfigById(configId);
    } else if (isNewMode) {
      setConfig(null);
      form.setFieldsValue({
        isProduction: false,
        isActive: true,
        linkedCustomerIds: [],
      });
      setLoading(false);
    } else {
      loadConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId, isNewMode]);

  const loadCustomers = async () => {
    try {
      const res: any = await apiService.getCustomersList();
      if (res?.success && Array.isArray(res.data)) {
        setCustomers(res.data.map((c: any) => ({ id: c.id, name: c.name })));
      } else if (Array.isArray(res?.data?.customers)) {
        setCustomers(res.data.customers.map((c: any) => ({ id: c.id, name: c.name })));
      }
    } catch (e) {
      console.error('Failed to load customers:', e);
    }
  };

  const loadConfigById = async (id: string) => {
    try {
      setLoading(true);
      const response: any = await apiService.getFedExConfigById(id);
      if (response?.success && response.data) {
        setConfig(response.data);
        form.setFieldsValue({
          clientId: response.data.clientId,
          accountNumber: response.data.accountNumber,
          isProduction: response.data.isProduction,
          isActive: response.data.isActive,
          linkedCustomerIds: (response.data.linkedCustomers || []).map((c: any) => c.id),
        });
        if (response.data.etdEnabled !== undefined) setEtdEnabled(response.data.etdEnabled);
        if (response.data.letterheadImageIndex) {
          setImageState(prev => ({
            ...prev,
            letterhead: { ...prev.letterhead, uploaded: true },
          }));
        }
        if (response.data.signatureImageIndex) {
          setImageState(prev => ({
            ...prev,
            signature: { ...prev.signature, uploaded: true },
          }));
        }
      } else {
        message.error('Configuration not found');
      }
    } catch (error: any) {
      console.error('Error loading FedEx config by id:', error);
      message.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await apiService.getFedExConfig();
      if (response.success && response.data) {
        setConfig(response.data);
        form.setFieldsValue({
          clientId: response.data.clientId,
          accountNumber: response.data.accountNumber,
          isProduction: response.data.isProduction,
          isActive: response.data.isActive,
        });
        // Set ETD state from config if available
        if (response.data.etdEnabled !== undefined) {
          setEtdEnabled(response.data.etdEnabled);
        }
        // Set image upload states based on stored image indexes
        // If letterheadImageIndex is set (e.g. 'IMAGE_1'), the image has been uploaded
        if (response.data.letterheadImageIndex) {
          setImageState(prev => ({
            ...prev,
            letterhead: { ...prev.letterhead, uploaded: true },
          }));
        }
        if (response.data.signatureImageIndex) {
          setImageState(prev => ({
            ...prev,
            signature: { ...prev.signature, uploaded: true },
          }));
        }
      }
    } catch (error: any) {
      console.error('Failed to load FedEx config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: any) => {
    try {
      setSaving(true);

      const payload = {
        clientId: values.clientId,
        clientSecret: values.clientSecret,
        accountNumber: values.accountNumber,
        isProduction: values.isProduction,
        isActive: values.isActive,
        linkedCustomerIds: values.linkedCustomerIds || [],
      };

      let response: any;
      if (configId) {
        response = await apiService.updateFedExConfigById(configId, payload);
      } else if (isNewMode) {
        response = await apiService.createFedExConfig(payload);
        if (response?.success && response?.data?.id) {
          message.success('FedEx 配置已建立');
          navigate(`/settings/fedex?configId=${response.data.id}`, { replace: true });
          return;
        }
      } else {
        // Legacy single-config path
        response = await apiService.saveFedExConfig({
          clientId: values.clientId,
          clientSecret: values.clientSecret,
          accountNumber: values.accountNumber,
          isProduction: values.isProduction,
          isActive: values.isActive,
        });
      }

      if (response?.success) {
        message.success('FedEx 配置已保存');
        if (configId) {
          loadConfigById(configId);
        } else if (response.data) {
          setConfig(response.data);
        }
        form.setFieldValue('clientSecret', ''); // Clear password field after save
        setTestResult(null);
      } else {
        message.error(response?.message || '保存失敗');
      }
    } catch (error: any) {
      message.error(error.message || '保存失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!configId) return;
    try {
      setSaving(true);
      const res: any = await apiService.deleteFedExConfigById(configId);
      if (res?.success) {
        message.success('Configuration deleted');
        navigate('/settings');
      } else {
        message.error(res?.message || res?.error || 'Failed to delete');
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    // Use a known FedEx test tracking number for sandbox
    const testTrackingNumber = form.getFieldValue('isProduction')
      ? '' // Don't auto-test in production
      : '794644790138'; // FedEx sandbox test tracking number

    if (!testTrackingNumber) {
      message.info('請手動輸入追蹤號碼進行測試');
      return;
    }

    try {
      setTesting(true);
      setTestResult(null);

      const response = await apiService.trackFedExShipment(testTrackingNumber, false);

      if (response.success) {
        setTestResult({
          success: true,
          message: `連接成功！追蹤狀態: ${response.data?.statusCode || 'OK'}`,
        });
      } else {
        setTestResult({
          success: false,
          message: response.message || response.data?.error || '連接失敗',
        });
      }
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.message || '連接測試失敗',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleImageUpload = async (
    file: File,
    imageType: 'LETTERHEAD' | 'SIGNATURE'
  ) => {
    const stateKey = imageType.toLowerCase() as 'letterhead' | 'signature';

    // Validate file type - FedEx only accepts PNG and GIF for letterhead/signature
    if (!['image/png', 'image/gif'].includes(file.type)) {
      message.error('FedEx 僅支持 PNG 或 GIF 圖片格式 (不支援 JPEG)');
      return;
    }

    // Validate file size (max 1MB)
    if (file.size > 1024 * 1024) {
      message.error('圖片大小不能超過 1MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageState(prev => ({
        ...prev,
        [stateKey]: {
          ...prev[stateKey],
          preview: e.target?.result as string,
        },
      }));
    };
    reader.readAsDataURL(file);

    // Validate dimensions
    const img = new Image();
    img.onload = async () => {
      const expectedWidth = imageType === 'LETTERHEAD' ? 700 : 240;
      const expectedHeight = imageType === 'LETTERHEAD' ? 50 : 25;

      if (img.width !== expectedWidth || img.height !== expectedHeight) {
        message.warning(
          `建議尺寸: ${expectedWidth}x${expectedHeight} 像素。當前尺寸: ${img.width}x${img.height}`
        );
      }

      // Upload the image
      setImageState(prev => ({
        ...prev,
        [stateKey]: { ...prev[stateKey], uploading: true },
      }));

      try {
        // Use IMAGE_1 for letterhead and IMAGE_2 for signature (FedEx allows up to 5 slots)
        const imageIndex = imageType === 'LETTERHEAD' ? 'IMAGE_1' : 'IMAGE_2';

        const response = await apiService.uploadFedExLetterheadImage(
          file,
          imageType,
          imageIndex as 'IMAGE_1' | 'IMAGE_2' | 'IMAGE_3' | 'IMAGE_4' | 'IMAGE_5'
        );

        if (response.success) {
          // After successful upload, save the image index to FedEx config
          // This is needed so shipments know which image slots to reference
          const currentConfig = config || { clientId: '', accountNumber: '', isProduction: false, isActive: true };
          const configUpdate: any = {
            ...currentConfig,
            etdEnabled: etdEnabled,
          };

          if (imageType === 'LETTERHEAD') {
            configUpdate.letterheadImageIndex = imageIndex;
          } else {
            configUpdate.signatureImageIndex = imageIndex;
          }

          // Only save if we have existing config
          if (config?.clientId) {
            await apiService.saveFedExConfig(configUpdate);
          }

          message.success(`${imageType === 'LETTERHEAD' ? '信頭' : '簽名'}圖片上傳成功`);
          setImageState(prev => ({
            ...prev,
            [stateKey]: { ...prev[stateKey], uploaded: true, uploading: false },
          }));

          // Reload config to get updated image indexes
          loadConfig();
        } else {
          throw new Error(response.message);
        }
      } catch (error: any) {
        message.error(`上傳失敗: ${error.message}`);
        setImageState(prev => ({
          ...prev,
          [stateKey]: { ...prev[stateKey], uploading: false },
        }));
      }
    };

    img.onerror = () => {
      message.error('無法讀取圖片');
    };

    const previewUrl = URL.createObjectURL(file);
    img.src = previewUrl;
  };

  const handleFileSelect = (imageType: 'LETTERHEAD' | 'SIGNATURE') => {
    const input = imageType === 'LETTERHEAD' ? letterheadInputRef : signatureInputRef;
    input.current?.click();
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    imageType: 'LETTERHEAD' | 'SIGNATURE'
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(file, imageType);
    }
    // Reset input value to allow re-uploading same file
    event.target.value = '';
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <SendOutlined /> FedEx 配置
      </Title>

      <Paragraph type="secondary">
        配置 FedEx API 憑證、ETD (電子貿易文件) 設置，以及海關清關所需的信頭和簽名圖片。
      </Paragraph>

      <Tabs defaultActiveKey="credentials">
        <TabPane
          tab={
            <span>
              <LinkOutlined /> API 憑證
            </span>
          }
          key="credentials"
        >
          <Row gutter={24}>
            <Col xs={24} lg={16}>
              <Spin spinning={loading}>
                <Card>
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSave}
                    initialValues={{
                      isProduction: false,
                      isActive: true,
                    }}
                  >
                    {/* API Credentials Section */}
                    <Title level={4}>API 憑證</Title>

                    <Form.Item
                      name="clientId"
                      label="Client ID (API Key)"
                      rules={[{ required: true, message: '請輸入 Client ID' }]}
                      tooltip="從 FedEx Developer Portal 獲取的 API Key"
                    >
                      <Input placeholder="輸入 FedEx API Client ID" />
                    </Form.Item>

                    <Form.Item
                      name="clientSecret"
                      label="Client Secret (Secret Key)"
                      rules={[
                        {
                          required: !config?.clientSecretSet,
                          message: '請輸入 Client Secret',
                        },
                      ]}
                      tooltip="從 FedEx Developer Portal 獲取的 Secret Key"
                      extra={
                        config?.clientSecretSet ? (
                          <Text type="success">
                            <CheckCircleOutlined /> 已設置 (留空則保持不變)
                          </Text>
                        ) : null
                      }
                    >
                      <Input.Password
                        placeholder={config?.clientSecretSet ? '******** (留空保持不變)' : '輸入 FedEx API Client Secret'}
                      />
                    </Form.Item>

                    <Form.Item
                      name="accountNumber"
                      label="Account Number"
                      rules={[{ required: true, message: '請輸入 FedEx Account Number' }]}
                      tooltip="您的 FedEx 帳號號碼"
                    >
                      <Input placeholder="輸入 FedEx Account Number" />
                    </Form.Item>

                    <Form.Item
                      name="linkedCustomerIds"
                      label="Linked Customers (連結客戶)"
                      extra="僅這些客戶的訂單會看到此 FedEx 帳號;留空表示這個帳號不限定客戶（會出現在 catch-all 列表中）"
                    >
                      <Select
                        mode="multiple"
                        placeholder="搜尋並選擇客戶"
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {customers.map((c) => (
                          <Select.Option key={c.id} value={c.id}>
                            {c.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Divider />

                    {/* Environment Settings */}
                    <Title level={4}>環境設置</Title>

                    <Form.Item
                      name="isProduction"
                      label="生產環境"
                      valuePropName="checked"
                      tooltip="開啟後將使用 FedEx 生產 API，否則使用沙盒測試 API"
                    >
                      <Switch
                        checkedChildren="生產"
                        unCheckedChildren="沙盒"
                      />
                    </Form.Item>

                    <Form.Item
                      name="isActive"
                      label="啟用整合"
                      valuePropName="checked"
                      tooltip="關閉後將停用 FedEx 追蹤功能"
                    >
                      <Switch
                        checkedChildren="啟用"
                        unCheckedChildren="停用"
                      />
                    </Form.Item>

                    <Divider />

                    {/* Test Result */}
                    {testResult && (
                      <Alert
                        message={testResult.success ? '連接測試成功' : '連接測試失敗'}
                        description={testResult.message}
                        type={testResult.success ? 'success' : 'error'}
                        showIcon
                        closable
                        onClose={() => setTestResult(null)}
                        style={{ marginBottom: 16 }}
                      />
                    )}

                    {/* Action Buttons */}
                    <Space>
                      <Button
                        type="primary"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={saving}
                      >
                        保存配置
                      </Button>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={handleTestConnection}
                        loading={testing}
                        disabled={!config?.clientSecretSet && !form.getFieldValue('clientSecret')}
                      >
                        測試連接
                      </Button>
                      {configId && (
                        <Popconfirm
                          title="Delete this configuration?"
                          description="This will not affect existing shipments but will remove this FedEx option from the dropdown."
                          onConfirm={handleDelete}
                          okText="Delete"
                          cancelText="Cancel"
                        >
                          <Button danger disabled={saving}>
                            刪除
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  </Form>
                </Card>
              </Spin>
            </Col>

            <Col xs={24} lg={8}>
              {/* Current Status */}
              <Card title="當前狀態" style={{ marginBottom: 16 }}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="配置狀態">
                    {config ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        已配置
                      </Tag>
                    ) : (
                      <Tag icon={<CloseCircleOutlined />} color="default">
                        未配置
                      </Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="環境">
                    {config?.isProduction ? (
                      <Tag color="red">生產環境</Tag>
                    ) : (
                      <Tag color="blue">沙盒測試</Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="整合狀態">
                    {config?.isActive ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        啟用
                      </Tag>
                    ) : (
                      <Tag icon={<CloseCircleOutlined />} color="default">
                        停用
                      </Tag>
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* Help Information */}
              <Card title="設置說明">
                <Alert
                  message="如何獲取 API 憑證"
                  description={
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                      <li>訪問 FedEx Developer Portal</li>
                      <li>註冊/登入開發者帳戶</li>
                      <li>創建新項目 (Project)</li>
                      <li>訂閱 Track API</li>
                      <li>獲取 API Key 和 Secret Key</li>
                      <li>記錄您的 Account Number</li>
                    </ol>
                  }
                  type="info"
                  icon={<InfoCircleOutlined />}
                  style={{ marginBottom: 16 }}
                />

                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    type="link"
                    icon={<LinkOutlined />}
                    href="https://developer.fedex.com"
                    target="_blank"
                  >
                    FedEx Developer Portal
                  </Button>
                  <Button
                    type="link"
                    icon={<LinkOutlined />}
                    href="https://developer.fedex.com/api/en-us/catalog/track.html"
                    target="_blank"
                  >
                    Track API 文檔
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane
          tab={
            <span>
              <FileProtectOutlined /> ETD 電子貿易文件
            </span>
          }
          key="etd"
        >
          <Row gutter={24}>
            <Col xs={24} lg={16}>
              <Card title="ETD 設置" style={{ marginBottom: 16 }}>
                <Alert
                  message="什麼是 ETD (電子貿易文件)？"
                  description="ETD 讓您可以無紙化提交海關清關所需的商業發票和其他文件。啟用 ETD 後，FedEx 會自動生成商業發票並提交給海關。"
                  type="info"
                  showIcon
                  style={{ marginBottom: 24 }}
                />

                <div style={{ marginBottom: 24 }}>
                  <Space align="center" style={{ marginBottom: 8 }}>
                    <Text strong>啟用 ETD 無紙化清關</Text>
                    <Switch
                      checked={etdEnabled}
                      onChange={setEtdEnabled}
                      checkedChildren="啟用"
                      unCheckedChildren="停用"
                    />
                  </Space>
                  <br />
                  <Text type="secondary">
                    啟用後，國際運單將自動使用電子貿易文件進行海關清關。
                  </Text>
                </div>

                {etdEnabled && (
                  <Alert
                    message="重要提示"
                    description="使用 ETD 需要先上傳信頭和簽名圖片。如果未上傳，創建運單時會收到 'COMMERCIAL_INVOICE requires electronic image' 錯誤。"
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
              </Card>

              <Card title="信頭和簽名圖片" extra={<FileImageOutlined />}>
                <Alert
                  message="圖片要求"
                  description={
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>信頭圖片 (Letterhead): 700 x 50 像素, PNG 或 JPEG 格式</li>
                      <li>簽名圖片 (Signature): 240 x 25 像素, PNG 或 JPEG 格式</li>
                      <li>圖片大小不超過 1MB</li>
                      <li>建議使用透明背景的 PNG 格式</li>
                    </ul>
                  }
                  type="info"
                  showIcon
                  style={{ marginBottom: 24 }}
                />

                <Row gutter={24}>
                  {/* Letterhead Upload */}
                  <Col xs={24} md={12}>
                    <Card
                      title="信頭圖片 (Letterhead)"
                      size="small"
                      style={{ marginBottom: 16 }}
                    >
                      <div style={{ marginBottom: 16 }}>
                        <Text type="secondary">尺寸: 700 x 50 像素</Text>
                      </div>

                      {imageState.letterhead.preview && (
                        <div
                          style={{
                            marginBottom: 16,
                            border: '1px solid #d9d9d9',
                            padding: 8,
                            borderRadius: 4,
                            backgroundColor: '#fafafa',
                          }}
                        >
                          <img
                            src={imageState.letterhead.preview}
                            alt="Letterhead Preview"
                            style={{ maxWidth: '100%', height: 'auto' }}
                          />
                        </div>
                      )}

                      <input
                        type="file"
                        ref={letterheadInputRef}
                        style={{ display: 'none' }}
                        accept="image/png,image/gif"
                        onChange={(e) => handleFileChange(e, 'LETTERHEAD')}
                      />

                      <Button
                        icon={<UploadOutlined />}
                        onClick={() => handleFileSelect('LETTERHEAD')}
                        loading={imageState.letterhead.uploading}
                        type={imageState.letterhead.uploaded ? 'default' : 'primary'}
                      >
                        {imageState.letterhead.uploaded ? '更換圖片' : '上傳圖片'}
                      </Button>

                      {imageState.letterhead.uploaded && (
                        <Tag
                          icon={<CheckCircleOutlined />}
                          color="success"
                          style={{ marginLeft: 8 }}
                        >
                          已上傳
                        </Tag>
                      )}
                    </Card>
                  </Col>

                  {/* Signature Upload */}
                  <Col xs={24} md={12}>
                    <Card
                      title="簽名圖片 (Signature)"
                      size="small"
                      style={{ marginBottom: 16 }}
                    >
                      <div style={{ marginBottom: 16 }}>
                        <Text type="secondary">尺寸: 240 x 25 像素</Text>
                      </div>

                      {imageState.signature.preview && (
                        <div
                          style={{
                            marginBottom: 16,
                            border: '1px solid #d9d9d9',
                            padding: 8,
                            borderRadius: 4,
                            backgroundColor: '#fafafa',
                          }}
                        >
                          <img
                            src={imageState.signature.preview}
                            alt="Signature Preview"
                            style={{ maxWidth: '100%', height: 'auto' }}
                          />
                        </div>
                      )}

                      <input
                        type="file"
                        ref={signatureInputRef}
                        style={{ display: 'none' }}
                        accept="image/png,image/gif"
                        onChange={(e) => handleFileChange(e, 'SIGNATURE')}
                      />

                      <Button
                        icon={<UploadOutlined />}
                        onClick={() => handleFileSelect('SIGNATURE')}
                        loading={imageState.signature.uploading}
                        type={imageState.signature.uploaded ? 'default' : 'primary'}
                      >
                        {imageState.signature.uploaded ? '更換圖片' : '上傳圖片'}
                      </Button>

                      {imageState.signature.uploaded && (
                        <Tag
                          icon={<CheckCircleOutlined />}
                          color="success"
                          style={{ marginLeft: 8 }}
                        >
                          已上傳
                        </Tag>
                      )}
                    </Card>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              {/* ETD Status */}
              <Card title="ETD 狀態" style={{ marginBottom: 16 }}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="ETD 功能">
                    {etdEnabled ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        已啟用
                      </Tag>
                    ) : (
                      <Tag icon={<CloseCircleOutlined />} color="default">
                        已停用
                      </Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="信頭圖片">
                    {imageState.letterhead.uploaded ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        已上傳
                      </Tag>
                    ) : (
                      <Tag icon={<CloseCircleOutlined />} color="warning">
                        未上傳
                      </Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="簽名圖片">
                    {imageState.signature.uploaded ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        已上傳
                      </Tag>
                    ) : (
                      <Tag icon={<CloseCircleOutlined />} color="warning">
                        未上傳
                      </Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="整體狀態">
                    {etdEnabled &&
                    imageState.letterhead.uploaded &&
                    imageState.signature.uploaded ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        準備就緒
                      </Tag>
                    ) : (
                      <Tag icon={<InfoCircleOutlined />} color="warning">
                        需要設置
                      </Tag>
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* ETD Help */}
              <Card title="ETD 說明">
                <Alert
                  message="ETD 使用流程"
                  description={
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                      <li>上傳公司信頭圖片 (700x50)</li>
                      <li>上傳簽名圖片 (240x25)</li>
                      <li>確保 ETD 已啟用</li>
                      <li>創建國際運單時自動使用</li>
                    </ol>
                  }
                  type="info"
                  icon={<InfoCircleOutlined />}
                  style={{ marginBottom: 16 }}
                />

                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    type="link"
                    icon={<LinkOutlined />}
                    href="https://developer.fedex.com/api/en-us/catalog/upload-documents/v1/docs.html"
                    target="_blank"
                  >
                    FedEx ETD 文檔
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
        </TabPane>
      </Tabs>

      {/* Image Preview Modal */}
      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </div>
  );
};

export default FedExSettings;
